export enum UserRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  BRANCH_MANAGER = 'branch_manager',
  EMPLOYEE = 'employee',
}

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
}

export enum CompanyStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
}

export enum OrderStatus {
  ASSIGNED = 'assigned',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  EXPIRED = 'expired',
}

export enum OrderType {
  ONE_TIME = 'one_time',
}

export enum LoanStatus {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  DEFAULTED = 'defaulted',
  CANCELLED = 'cancelled',
}

export enum PaymentFrequency {
  WEEKLY = 'weekly',
  BIWEEKLY = 'biweekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
}

export enum OrderPaymentStatus {
  UNPAID = 'unpaid',
  PARTIALLY_PAID = 'partially_paid',
  PAID = 'paid',
  OVERPAID = 'overpaid',
}

export enum ProductStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DISCONTINUED = 'discontinued',
}

export enum SaleStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded',
}

export enum PaymentMethod {
  CASH = 'cash',
  CREDIT_CARD = 'credit_card',
  DEBIT_CARD = 'debit_card',
  BANK_TRANSFER = 'bank_transfer',
  DIGITAL_WALLET = 'digital_wallet',
}

export enum CustomerStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  BLOCKED = 'blocked',
}

export enum CustomerType {
  INDIVIDUAL = 'individual',
  BUSINESS = 'business',
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
  PENDING = 'pending',
  SUSPENDED = 'suspended',
}

export enum PaymentStatus {
  PENDING = 'pending',
  PAID = 'paid',
  FAILED = 'failed',
  REFUNDED = 'refunded',
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: UserRole;
  status: UserStatus;
  avatar?: string;
  lastLoginAt?: Date;
  companyId?: string;
  locationId?: string;
  company?: Company;
  location?: Location;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

export interface Company {
  id: string;
  name: string;
  description?: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  website?: string;
  status: CompanyStatus;
  logo?: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  users?: User[];
  currencies?: Currency[];
  defaultCurrencyId?: string;
  defaultCurrency?: Currency;
  currency?: Currency;
}

export interface Order {
  id: string;
  companyId: string;
  locationId?: string;
  assignedBy: string;
  assignedTo: string;
  customerId?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  status: OrderStatus;
  type: OrderType;
  paymentStatus: OrderPaymentStatus;
  totalAmount: number;
  subtotal: number;
  tax: number;
  discount: number;
  paidAmount: number;
  remainingAmount: number;
  currencyCode?: string;
  exchangeRate: number;
  assignedDate: Date;
  dueDate?: Date;
  completedDate?: Date;
  firstPaymentDate?: Date;
  lastPaymentDate?: Date;
  notes?: string;
  employeeNotes?: string;
  adminNotes?: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  company?: Company;
  location?: Location;
  assignedByUser?: User;
  assignedToUser?: User;
  items?: OrderItem[];
}

export interface OrderItem {
  id: string;
  orderId: string;
  productId: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  total: number;
  notes?: string;
  product?: Product;
}

export interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  cost: number;
  inventory: number;
  minInventory: number;
  sku?: string;
  barcode?: string;
  category?: string;
  brand?: string;
  image?: string;
  status: ProductStatus;
  weight: number;
  dimensions?: string;
  tags?: string[];
  companyId: string;
  currencyId: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  company?: Company;
  currency?: Currency;
}

export interface Currency {
  id: string;
  code: string;
  name: string;
  symbol?: string;
  exchangeRate: number | string;
  isBase: boolean;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

export interface CreateCurrencyDto {
  code: string;
  name: string;
  symbol?: string;
  exchangeRate?: number;
  isBase?: boolean;
  isActive?: boolean;
}

export interface UpdateCurrencyDto {
  code?: string;
  name?: string;
  symbol?: string;
  exchangeRate?: number;
  isBase?: boolean;
  isActive?: boolean;
}

export interface Sale {
  id: string;
  invoiceNumber: string;
  orderId?: string; // Optional reference to the order (null for subscription/loan payments)
  loanId?: string; // Optional reference to the loan (for loan payments)
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  status: SaleStatus;
  paymentMethod: PaymentMethod;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  notes?: string;
  currencyCode?: string;
  exchangeRate: number;
  completedAt?: Date;
  completedBy?: string;
  userId: string;
  customerId?: string;
  subscriptionId?: string; // Reference to subscription for subscription payments
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  user?: User;
  order?: Order; // Relationship to the source order
  subscription?: Subscription; // Relationship to the subscription
  loan?: Loan; // Relationship to the source loan
  items?: SaleItem[];
}

export interface SaleItem {
  id: string;
  saleId: string;
  productId: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  total: number;
  product?: Product;
}

export interface Customer {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  type: CustomerType;
  status: CustomerStatus;
  taxId?: string;
  website?: string;
  notes?: string;
  contactPerson?: string;
  contactPhone?: string;
  contactEmail?: string;
  creditLimit: number;
  currentBalance: number;
  paymentTerms?: string;
  companyId: string;
  locationId?: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  company?: Company;
  location?: Location;
  sales?: Sale[];
  orders?: Order[];
}

export interface CreateUserDto {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone?: string;
  role: UserRole;
  status?: UserStatus;
  companyId?: string;
}

export interface UpdateUserDto {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  phone?: string;
  role?: UserRole;
  status?: UserStatus;
  companyId?: string;
}

export interface CreateCompanyDto {
  name: string;
  description?: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  website?: string;
  status?: CompanyStatus;
  logo?: string;
  defaultCurrencyId?: string;
}

export interface UpdateCompanyDto {
  name?: string;
  description?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  website?: string;
  status?: CompanyStatus;
  logo?: string;
  defaultCurrencyId?: string;
}

export interface CreateOrderDto {
  companyId: string;
  assignedTo: string;
  customerId?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  customerAddress?: string;
  status?: OrderStatus;
  type: OrderType;
  subtotal: number;
  tax: number;
  discount: number;
  currencyCode?: string;
  exchangeRate?: number;
  assignedDate: string;
  dueDate?: string;
  notes?: string;
  items: CreateOrderItemDto[];
}

export interface PaymentItemDto {
  productId: string;
  quantity: number;
  unitPrice: number;
  discount?: number;
  notes?: string;
}

export interface PayOrderDto {
  employeeNotes?: string;
  actualSubtotal?: number;
  actualTax?: number;
  actualDiscount?: number;
  items: PaymentItemDto[];
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  notes?: string;
  currencyCode?: string;
  exchangeRate?: number;
}

export interface CreateOrderItemDto {
  productId: string;
  quantity: number;
  unitPrice: number;
  discount?: number;
  notes?: string;
}

export interface UpdateOrderDto {
  assignedTo?: string;
  customerId?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  customerAddress?: string;
  status?: OrderStatus;
  type?: OrderType;
  subtotal?: number;
  tax?: number;
  discount?: number;
  currencyCode?: string;
  exchangeRate?: number;
  assignedDate?: string;
  dueDate?: string;
  notes?: string;
  employeeNotes?: string;
  adminNotes?: string;
}

export interface CreateProductDto {
  name: string;
  description?: string;
  price: number;
  cost: number;
  inventory: number;
  minInventory?: number;
  sku?: string;
  barcode?: string;
  category?: string;
  brand?: string;
  image?: string;
  status?: ProductStatus;
  weight?: number;
  dimensions?: string;
  tags?: string[];
  companyId: string;
  currencyId: string;
}

export interface UpdateProductDto {
  name?: string;
  description?: string;
  price?: number;
  cost?: number;
  inventory?: number;
  minInventory?: number;
  sku?: string;
  barcode?: string;
  category?: string;
  brand?: string;
  image?: string;
  status?: ProductStatus;
  weight?: number;
  dimensions?: string;
  tags?: string[];
  currencyId?: string;
}

export interface CreateSaleDto {
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  notes?: string;
  currencyCode?: string;
  exchangeRate?: number;
  paymentMethod?: PaymentMethod;
  customerId?: string;
  items: CreateSaleItemDto[];
}

// New DTO for creating sales from orders
export interface CreateSaleFromOrderDto {
  notes?: string;
  tax?: number;
  discount?: number;
  paymentMethod: PaymentMethod;
  items: CreateSaleItemFromOrderDto[];
}

export interface CreateSaleItemDto {
  productId: string;
  quantity: number;
  unitPrice: number;
  discount?: number;
}

export interface CreateSaleItemFromOrderDto {
  productId: string;
  quantity: number;
  unitPrice: number;
  discount?: number;
}

export interface UpdateSaleDto {
  subtotal?: number;
  tax?: number;
  discount?: number;
  total?: number;
  status?: SaleStatus;
  paymentMethod?: PaymentMethod;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  notes?: string;
  customerId?: string;
  currencyCode?: string;
  exchangeRate?: number;
}

export interface CreateCustomerDto {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  type?: CustomerType;
  status?: CustomerStatus;
  taxId?: string;
  website?: string;
  notes?: string;
  contactPerson?: string;
  contactPhone?: string;
  contactEmail?: string;
  creditLimit?: number;
  currentBalance?: number;
  paymentTerms?: string;
  companyId: string;
}

export interface UpdateCustomerDto {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  type?: CustomerType;
  status?: CustomerStatus;
  taxId?: string;
  website?: string;
  notes?: string;
  contactPerson?: string;
  contactPhone?: string;
  contactEmail?: string;
  creditLimit?: number;
  currentBalance?: number;
  paymentTerms?: string;
}

export interface Subscription {
  id: string;
  companyId: string;
  basePrice: number;
  pricePerLocation: number;
  activeLocations: number;
  totalAmount: number;
  status: SubscriptionStatus;
  paymentStatus: PaymentStatus;
  paymentMethod: PaymentMethod;
  startDate: Date;
  endDate: Date;
  paidAt?: Date;
  invoiceNumber?: string;
  receiptNumber?: string;
  notes?: string;
  adminNotes?: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  company?: Company;
  currencyId?: string;
  currency?: Currency;
}

export interface CreateSubscriptionDto {
  companyId: string;
  basePrice: number;
  pricePerLocation: number;
  activeLocations: number;
  totalAmount: number;
  status?: SubscriptionStatus;
  paymentStatus?: PaymentStatus;
  paymentMethod?: PaymentMethod;
  startDate: string;
  endDate: string;
  notes?: string;
  adminNotes?: string;
}

export interface UpdateSubscriptionDto {
  basePrice?: number;
  pricePerLocation?: number;
  activeLocations?: number;
  totalAmount?: number;
  status?: SubscriptionStatus;
  paymentStatus?: PaymentStatus;
  paymentMethod?: PaymentMethod;
  startDate?: string;
  endDate?: string;
  notes?: string;
  adminNotes?: string;
}

export interface ProcessPaymentDto {
  paymentMethod: PaymentMethod;
  receiptNumber?: string;
  notes?: string;
}

export interface TimeSeriesPoint {
  date: string;
  value: number;
}

export interface ReportingRangeParams {
  from: string; // YYYY-MM-DD or ISO date
  to: string;   // YYYY-MM-DD or ISO date
}

// Location types
export interface Location {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  companyId: string;
  managerId?: string;
  currencyId: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  company?: Company;
  manager?: User;
  currency?: Currency;
  employees?: User[];
}

export interface CreateLocationDto {
  name: string;
  companyId: string;
  currencyId: string;
  address?: string;
  phone?: string;
  email?: string;
  managerId?: string;
  isActive?: boolean;
}

export interface UpdateLocationDto {
  name?: string;
  companyId?: string;
  currencyId?: string;
  address?: string;
  phone?: string;
  email?: string;
  managerId?: string;
  isActive?: boolean;
}

// Loan interfaces
export interface Loan {
  id: string;
  companyId: string;
  locationId: string;
  borrowerId: string;
  loanOfficerId: string;
  approvedBy: string;

  // Financial details
  principalAmount: number;
  currentBalance: number;
  totalPaid: number;
  totalInterestPaid: number;
  totalLateFees: number;

  // Terms
  monthlyInterestRate: number;
  lateFeeAmount: number;
  lateFeePercentage: number;
  gracePeriodDays: number;
  paymentFrequency: PaymentFrequency;
  installments: number;
  scheduledPaymentAmount: number;

  // Status and dates
  status: LoanStatus;
  startDate: Date;
  endDate: Date;
  nextPaymentDue: Date | null;
  lastPaymentDate: Date | null;
  completedDate: Date | null;

  // Notes
  terms: string | null;
  notes: string | null;
  internalNotes: string | null;
  paymentSchedule: any;

  // Timestamps
  createdAt: Date;
  updatedAt: Date;

  // Relationships (optional)
  company?: Company;
  location?: Location;
  borrower?: Customer;
  loanOfficer?: User;
  approver?: User;
  payments?: LoanPayment[];

  // Calculated fields
  remainingBalance?: number;
  overdueDays?: number;
  nextPaymentAmount?: number;
  isOverdue?: boolean;
  projectedPayoffDate?: Date;
}

export interface LoanPayment {
  id: string;
  amount: number;
  paymentDate: Date;
  interestAmount: number;
  principalAmount: number;
  lateFeeAmount: number;
  notes: string;
}

export interface CreateLoanDto {
  companyId: string;
  locationId: string;
  borrowerId: string;
  loanOfficerId: string;
  approvedBy: string;
  principalAmount: number;
  monthlyInterestRate: number;
  lateFeeAmount?: number;
  lateFeePercentage?: number;
  gracePeriodDays?: number;
  paymentFrequency: PaymentFrequency;
  installments: number;
  scheduledPaymentAmount?: number;
  startDate: string;
  endDate: string;
  nextPaymentDue?: string;
  terms?: string;
  notes?: string;
  internalNotes?: string;
}

export interface UpdateLoanDto {
  locationId?: string;
  borrowerId?: string;
  loanOfficerId?: string;
  approvedBy?: string;
  principalAmount?: number;
  currentBalance?: number;
  monthlyInterestRate?: number;
  lateFeeAmount?: number;
  lateFeePercentage?: number;
  gracePeriodDays?: number;
  paymentFrequency?: PaymentFrequency;
  installments?: number;
  scheduledPaymentAmount?: number;
  status?: LoanStatus;
  startDate?: string;
  endDate?: string;
  nextPaymentDue?: string;
  terms?: string;
  notes?: string;
  internalNotes?: string;
}

export interface RecordLoanPaymentDto {
  loanId: string;
  paymentAmount: number;
  principalAmount?: number;
  interestAmount?: number;
  lateFeeAmount?: number;
  paymentDate?: string;
  paymentMethod?: string;
  notes?: string;
  receiptNumber?: string;
}
